import React, { useState, useEffect, useCallback } from "react";
import {
  User,
  Mail,
  BarChart2,
  Zap,
  Activity,
  MessageSquare,
  Repeat,
  Puzzle,
  Calendar,
} from "lucide-react";
import Combobox from "../../components/Combobox/Combobox";
import UserModal from "../UserManagement/UserModal/UserModal";
// import PlatformIntegrations from "./PlatformIntegrations/PlatformIntegrations";
import "./Profile.css";
import useUser from "../../../hooks/useUser";
import { useProfile } from "../../../context";
import customFetch from "../../../helpers/axios";
import { normalizeApiResponse } from "../../../helpers/apiUtils";
import { useTranslation } from "react-i18next";
import { setUserCookie } from "../../../context/cookieUtils";

export default function Profile() {
  const { user } = useUser();
  // Derive stable primitives for effect deps
  const userId = user?.id;
  const userAccess = user?.access;
  const { t } = useTranslation("profile");
  const { dateRange, updateDateRange } = useProfile();

  const [accountActivity, setAccountActivity] = useState({
    period: { start_date: null, end_date: null, days: null },
    summary: {
      total_credits: 0,
      total_messages: 0,
      total_iterations: 0,
      total_tool_calls: 0,
      total_prompts: 0,
      days_active: 0,
      avg_daily_credits: 0,
      last_activity_date: null,
    },
    today: {
      date: null,
      credits_used: 0,
      credit_limit: 0,
      credits_remaining: 0,
    },
    models_used: [],
    daily: [],
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [creditLimits, setCreditLimits] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Normaliza la forma del usuario para unificar organization/organization_id

  const normalizeUserShape = (u) => {
    if (!u || typeof u !== "object") return u;
    const out = { ...u };
    // Soportar diferentes formas de rol: role (objeto) o role_id (id u objeto)
    if (!out.role_id && out.role) {
      out.role_id = out.role; // puede ser objeto {id, name} o id
    }
    // Si role_id es objeto, asegurar id accesible
    if (out.role_id && typeof out.role_id === "object") {
      out.role_id = {
        id: out.role_id.id ?? out.role_id,
        name: out.role_id.name ?? out.role_id?.label ?? out.role_id?.toString?.(),
        credit_limit: out.role_id.credit_limit,
        organization: out.role_id.organization,
      };
    }
    // Si organization_id viene como objeto, úsalo como organization y guarda el id numérico
    if (out.organization_id && typeof out.organization_id === "object") {
      out.organization = out.organization_id;
      out.organization_id =
        out.organization?.id ?? out.organization_id?.id ?? out.organization_id;
    }
    // Si organization es objeto, asegura organization_id sea el id
    if (out.organization && typeof out.organization === "object") {
      out.organization_id = out.organization?.id ?? out.organization_id;
    }
    // Si organization es un string plano, guárdalo como organization_name para mostrarlo
    if (typeof out.organization === "string") {
      out.organization_name = out.organization;
    }
    return out;
  };

  // Handler para guardar cambios de perfil
  const handleSaveProfile = async (dataToSave) => {
    try {
      const userId = currentUser.id;
      const headers =
        user && user.access
          ? { headers: { Authorization: `Bearer ${user.access}` } }
          : {};
      const patchResponse = await customFetch.patch(
        `/api/users/${userId}/`,
        dataToSave,
        headers
      );
      const normalized = normalizeApiResponse(patchResponse.data);
      const updatedUser = normalizeUserShape(normalized.results[0]);

      setUserCookie({
        ...user,
        ...updatedUser,
        access: user?.access,
        refresh: user?.refresh,
      });
      if (typeof window !== "undefined" && window.owlyManageSetUser) {
        window.owlyManageSetUser({
          ...user,
          ...updatedUser,
          access: user?.access,
          refresh: user?.refresh,
        });
      }
      // Update local currentUser state as we are no longer maintaining a users list
      setCurrentUser(updatedUser);
      setIsModalOpen(false);
    } catch (e) {
      alert("Error updating profile");
      console.error("Error PATCH profile:", e?.response || e);
    }
  };

  // Fetch current user's data
  const fetchCurrentUser = useCallback(async () => {
    if (!user?.id) return;
    try {
      const headers = user?.access
        ? { headers: { Authorization: `Bearer ${user.access}` } }
        : {};
      const response = await customFetch.get(`/api/users/${user.id}/`, headers);
      const normalized = normalizeApiResponse(response.data);
      const item = Array.isArray(normalized.results)
        ? normalized.results[0]
        : normalized;
      setCurrentUser(normalizeUserShape(item));
    } catch (error) {
      console.error("Failed to fetch current user:", error);
    }
  }, [user?.id, user?.access]);

  // Initialize currentUser from context user to avoid empty UI while fetching
  useEffect(() => {
    if (user && (user.id || user.email)) {
      setCurrentUser(normalizeUserShape(user));
    } else {
      setCurrentUser(null);
    }
  }, [user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchRolesAndCreditLimits = async () => {
      try {
        const res = await customFetch.get(
          "/api/roles/",
          userAccess ? { headers: { Authorization: `Bearer ${userAccess}` } } : {}
        );
        const normalized = normalizeApiResponse(res.data);
        const data = normalized.results || [];

        const roles = data.map((role) => ({
          value: role.id,
          label: role.name,
        }));
        setRoleOptions(roles);

        const limits = {};
        data.forEach((role) => {
          limits[role.id] = role.credit_limit;
        });
        setCreditLimits(limits);
      } catch (error) {
        setRoleOptions([]);
        setCreditLimits({});
      }
    };

    if (userAccess) {
      fetchCurrentUser();
      fetchRolesAndCreditLimits();
    }
  }, [userId, userAccess, fetchCurrentUser]);

  // Opciones de rango de fechas
  const dateRangeOptions = [
    { value: "1d", label: t("activity.dateRange.1d") },
    { value: "7d", label: t("activity.dateRange.7d") },
    { value: "30d", label: t("activity.dateRange.30d") },
    { value: "90d", label: t("activity.dateRange.90d") },
    { value: "365d", label: t("activity.dateRange.365d") },
  ];

  const handleDateRangeChange = (val) => {
    updateDateRange(val);
    // Actualizar Account Activity con el nuevo rango
    const days = parseInt(val);
    fetchAccountActivity(days);
  };


  // Integración real con backend para Account Activity
  const fetchAccountActivity = async (days) => {
    try {
      const headers =
        user && user.access
          ? { headers: { Authorization: `Bearer ${user.access}` } }
          : {};
      const { data } = await customFetch.get(
        `/api/logic_layer/usage/account-activity/?days=${days}`,
        headers
      );
      setAccountActivity(data);
    } catch (e) {
      console.error("Error fetching account activity", e?.response || e);
    }
  };

  // Carga inicial de Account Activity basado en el rango seleccionado
  useEffect(() => {
    if (user && user.access) {
      const days = parseInt(dateRange);
      fetchAccountActivity(days);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dateRange]);

  // Use the currentUser state that we fetch directly puede editar su perfil
  const canEditProfile = !!(currentUser?.is_staff || currentUser?.is_superuser);

  // Helpers de formato
  const fmt = new Intl.NumberFormat();
  const shortNum = (n) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
        ? `${(n / 1_000).toFixed(1)}k`
        : `${n}`;
  const toPrettyDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString() : "-";

  return (
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-info-header">
            <div
              className={`profile-avatar ${!canEditProfile ? "no-hover" : ""}`}
              role={canEditProfile ? "button" : undefined}
              tabIndex={canEditProfile ? 0 : -1}
              title={canEditProfile ? t("header.editProfileTooltip") : undefined}
              data-tooltip={canEditProfile ? t("header.editProfileTooltip") : undefined}
              onClick={canEditProfile ? () => setIsModalOpen(true) : undefined}
              onKeyDown={
                canEditProfile
                  ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsModalOpen(true);
                    }
                  }
                  : undefined
              }
              style={{ cursor: canEditProfile ? "pointer" : "default" }}
            >
              <User size={44} />
            </div>
            <div className="profile-info-main">
              <h2>
                {(() => {
                  if (!currentUser) return t("header.userFallback");
                  const first = currentUser.first_name?.trim?.();
                  const last = currentUser.last_name?.trim?.();
                  const full = [first, last].filter(Boolean).join(" ");
                  return full || currentUser.name || currentUser.username || t("header.userFallback");
                })()}
              </h2>
              <div className="profile-email">
                <Mail size={18} />
                {currentUser?.email}
                {(() => {
                  if (!currentUser) return null;
                  // Prefer new role_id shape. Fallback to legacy level field if present.
                  const roleId =
                    (typeof currentUser.role_id === "object"
                      ? currentUser.role_id?.id
                      : currentUser.role_id) ?? currentUser.level;
                  if (!roleId) return null;
                  const match = roleOptions.find((l) => l.value === roleId);
                  const roleName =
                    match?.label ??
                    (typeof currentUser.role_id === "object"
                      ? currentUser.role_id?.name
                      : undefined) ??
                    roleId;
                  return (
                    <span className={`user-badge filter-${roleId}`}>
                      {roleName}
                    </span>
                  );
                })()}
              </div>
              {(() => {
                const used = Number(accountActivity?.today?.credits_used || 0);
                const total = Number(accountActivity?.today?.credit_limit || 0);
                if (!total || total <= 0) return null;
                const pct = Math.min(100, Math.round((used / total) * 100));
                const hue = 120 - pct * 1.2; // green -> red
                const barColor = `hsl(${Math.max(0, Math.min(120, hue))}, 70%, 45%)`;
                return (
                  <div className="profile-consumption">
                    <span className="profile-credits-badge" title={`${fmt.format(Math.round(used))} / ${fmt.format(Math.round(total))} (${pct}%)`}>
                      <div className="credits-progress">
                        <div className="credits-progress-bar">
                          <div
                            className="credits-progress-bar-inner"
                            style={{ width: `${pct}%`, backgroundColor: barColor }}
                          />
                        </div>
                        <div className="credits-progress-text">
                          {t("header.credits.consumption")} {fmt.format(Math.round(used))} / {fmt.format(Math.round(total))} ({pct}%)
                        </div>
                      </div>
                    </span>
                  </div>
                );
              })()}
              {/* Campos extra */}
              <div className="profile-extra-info">
                <span>
                  <b>{t("header.fields.created")}:</b>{" "}
                  {currentUser?.date_joined
                    ? new Date(currentUser.date_joined).toLocaleDateString()
                    : "-"}
                </span>
                <span>
                  <b>{t("header.fields.active")}:</b> {currentUser?.is_active ? t("header.fields.yes") : t("header.fields.no")}
                </span>
                <span>
                  <b>{t("header.fields.staff")}:</b> {currentUser?.is_staff ? t("header.fields.yes") : t("header.fields.no")}
                </span>
                <span>
                  <b>{t("header.fields.superuser")}:</b> {currentUser?.is_superuser ? t("header.fields.yes") : t("header.fields.no")}
                </span>
                <span>
                  <b>{t("header.fields.organization")}:</b> {currentUser?.organization?.name || currentUser?.organization_name || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-activity">
          <span>
            <h1 className="profile-activity-title">{t("activity.title")}</h1>
            <div className="profile-activity-date">
              <Calendar size={14} />
              <span style={{ marginLeft: 6 }}>
                {toPrettyDate(accountActivity?.period?.start_date)} –{" "}
                {toPrettyDate(accountActivity?.period?.end_date)}
              </span>
              <span style={{ marginLeft: 10, color: "#7a7a9f" }}>
                · {accountActivity?.period?.days ? t("activity.periodDays", { count: accountActivity.period.days }) : "-"}
              </span>
            </div>
          </span>
          <Combobox
            className="usage-date-range"
            value={dateRange}
            onChange={handleDateRangeChange}
            options={dateRangeOptions}
          />
        </div>

        {/* Account Activity colorful cards */}
        <div className="activity-cards">
          <div className="activity-card gradient-purple">
            <div className="card-icon">
              <Activity size={18} />
            </div>
            <div className="card-title">{t("activity.cards.credits.title")}</div>
            <div className="card-value">
              {fmt.format(Math.round(accountActivity.summary.total_credits))}
            </div>
            <div className="card-sub">
              {t("activity.cards.credits.avg", { value: fmt.format(Math.round(accountActivity.summary.avg_daily_credits)) })}
            </div>
          </div>
          <div className="activity-card gradient-blue">
            <div className="card-icon">
              <MessageSquare size={18} />
            </div>
            <div className="card-title">{t("activity.cards.messages.title")}</div>
            <div className="card-value">
              {fmt.format(accountActivity.summary.total_messages)}
            </div>
            <div className="card-sub">
              {t("activity.cards.messages.daysActive", { count: accountActivity.summary.days_active })}
            </div>
          </div>
          <div className="activity-card gradient-teal">
            <div className="card-icon">
              <Repeat size={18} />
            </div>
            <div className="card-title">{t("activity.cards.iterations.title")}</div>
            <div className="card-value">
              {fmt.format(accountActivity.summary.total_iterations)}
            </div>
            <div className="card-sub">
              {t("activity.cards.iterations.toolCalls", { count: fmt.format(accountActivity.summary.total_tool_calls) })}
            </div>
          </div>
          <div className="activity-card gradient-orange">
            <div className="card-icon">
              <Puzzle size={18} />
            </div>
            <div className="card-title">{t("activity.cards.prompts.title")}</div>
            <div className="card-value">
              {fmt.format(accountActivity.summary.total_prompts)}
            </div>
            <div className="card-sub">
              {t("activity.cards.prompts.lastActivity", { date: toPrettyDate(accountActivity.summary.last_activity_date) })}
            </div>
          </div>
        </div>

        {/* Models usage chips */}
        <div className="models-usage">
          <div className="models-usage-title">
            <BarChart2 size={18} /> <span>{t("activity.models.title")}</span>
          </div>
          <div className="models-usage-grid">
            {accountActivity.models_used.map((m) => (
              <div className="model-chip" key={m.id}>
                <div className="model-name">{m.name}</div>
                <div className="model-stats">{t("activity.models.stats", { credits: shortNum(Math.round(m.credits)), messages: m.messages })}</div>
                <div className="model-bar">
                  <div
                    className="model-bar-inner"
                    style={{
                      width: `${Math.min(
                        100,
                        (m.credits /
                          (accountActivity.summary.total_credits || 1)) *
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily activity mini bars */}
        <div className="daily-activity">
          <div className="daily-activity-title">
            <Zap size={18} /> <span>{t("activity.daily.title")}</span>
          </div>
          <div className="daily-bars">
            {(() => {
              const max = Math.max(
                ...accountActivity.daily.map((d) => d.credits),
                1
              );
              return accountActivity.daily.map((d) => (
                <div
                  className="daily-bar"
                  key={d.date}
                  title={t("activity.daily.barTitle", { date: d.date, credits: fmt.format(d.credits) })}
                >
                  <div
                    className="daily-bar-inner"
                    style={{
                      height: `${Math.max(
                        8,
                        Math.round((d.credits / max) * 100)
                      )}%`,
                    }}
                  />
                  <div className="daily-bar-label">
                    {new Date(d.date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
        {/* <PlatformIntegrations /> */}
        {canEditProfile && (
          <UserModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            user={currentUser}
            creditLimits={creditLimits}
            roleOptions={roleOptions}
            onSave={handleSaveProfile}
          />
        )}
      </div>
  );
}
